import { type CSSProperties, type FC, type ReactNode } from 'react';
import { Checkbox, Dropdown, Flex } from 'antd';
import ShipmentOverflowTooltip from './ShipmentOverflowTooltip';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  DotsIcon,
  getShipmentLegModeIcon,
} from '@/assets/icons/icons';
import { getShipmentLocationParsedAddress } from '@/common/utils/utils';
import StatusTag from '@/pages/Shipments/components/StatusTag';
import type { StatusType } from '@/pages/Shipments/statusType';
import DeliveryStatusTag from '@/pages/Shipments/MapExternalView/DeliveryStatusTag';
import ExportCOCWrapper from './ExportCOCWrapper';
import SchedulePickupMenuItem from './SchedulePickupMenuItem';
import type { ShipmentRow } from '../../types';
import {
  MENU_ITEMS,
  type ShipmentTableActionKey,
} from './shipmentTableConstants';
import ShipmentTableProductCell from './ShipmentTableProductCell';
import ShipmentActivityCell from './ShipmentActivityCell';
import {
  formatCarrierName,
  formatEstimatedArrival,
  getLocationName,
  getCustomerReference,
  getShipmentRowId,
  getTeams,
  textOrEmpty,
} from './shipmentTableUtils';
import TeamListWithDots from '@/pages/Shipments/components/TeamListWithDots';
import ShipmentTablePackageBadge from './ShipmentTablePackageBadge';
import type { ShipmentVirtualTableProps } from './ShipmentVirtualTable.types';
import { getLegModeDisplay, getLegModeIconType } from './legModeUtils';
import {
  getExecutionLegDisplayCount,
  isElSubRow,
} from './shipmentAccordionUtils';
import { legTypes } from '@/apis/shipment';

interface ShipmentGridTextCellProps {
  text: string;
  strong?: boolean;
  maxWidth?: number | string;
  width?: number | string;
  fontSize?: number;
  color?: string;
  className?: string;
  style?: CSSProperties;
}

const ShipmentGridTextCell: FC<ShipmentGridTextCellProps> = ({
  text,
  strong,
  maxWidth,
  width = '100%',
  fontSize,
  color,
  className,
  style,
}) => {
  return (
    <ShipmentOverflowTooltip
      text={text}
      className={`shipment-grid-text-cell${className ? ` ${className}` : ''}`}
      style={{
        width,
        minWidth: 0,
        maxWidth: maxWidth ?? 180,
        fontWeight: strong ? 600 : 400,
        fontSize: fontSize ?? 12,
        color,
        ...style,
      }}
    />
  );
};

const toTeamBadges = (teams: ReturnType<typeof getTeams>) =>
  teams.map(({ name, color }) => ({ name, color: color ?? undefined }));

const renderTextCell = (
  text: string,
  options: Omit<ShipmentGridTextCellProps, 'text'> = {}
) => <ShipmentGridTextCell text={text} {...options} />;

const normalizeStatusText = (value?: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const isDeliveredShipment = (record: ShipmentRow) =>
  normalizeStatusText(record.status) === 'delivered';

const getTimelinessPill = (record: ShipmentRow) => {
  const rawStatus = String(record.delivery_status ?? '').trim();

  if (!rawStatus) return null;

  const normalized = rawStatus.toLowerCase();

  if (normalized.includes('late')) {
    return rawStatus.replace(/\s*-\s*in transit\s*/i, '').trim();
  }

  if (normalized.includes('early')) {
    return rawStatus;
  }

  return null;
};

export const trackingPillStyle: CSSProperties = {
  height: 20,
  lineHeight: '18px',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 10,
  fontWeight: 600,
};

const ShipmentMultilineTooltipCell: FC<{
  tooltipLines: Array<string | null | undefined | false>;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}> = ({ className, style, children }) => {
  return (
    // eslint-disable-next-line react/forbid-dom-props -- caller-supplied style merged at runtime
    <div className={className} style={{ minWidth: 0, width: '100%', ...style }}>
      {children}
    </div>
  );
};

const ShipmentReferenceNumber: FC<{
  text: string;
  useAvailableWidth?: boolean;
}> = ({ text, useAvailableWidth }) => {
  return (
    <ShipmentOverflowTooltip
      text={text}
      className='shipment-grid-text-cell shipment-grid-reference-number'
      style={{
        width: '100%',
        maxWidth: useAvailableWidth ? '100%' : 210,
        fontWeight: 600,
        fontSize: 14,
        lineHeight: '24px',
        margin: 0,
        padding: 0,
      }}
    />
  );
};

export const renderReferenceCell = (
  record: ShipmentRow,
  selected: boolean,
  checked: boolean | undefined,
  onSelectChange: ShipmentVirtualTableProps['onSelectChange'],
  useAvailableWidth = false,
  activeLegType?: 'CL' | 'EL'
) => {
  const rowId = getShipmentRowId(record);
  const legMode = getLegModeDisplay(record);

  // Use the same icon/timeline layout as the customer execution-leg table.
  if (isElSubRow(record)) {
    return (
      <div className='ref-cell-flex-row-center'>
        <span
          className='shipment-grid-reference-leg-icon shipment-grid-reference-leg-icon--sub'
          aria-hidden='true'
        >
          {getShipmentLegModeIcon(getLegModeIconType(legMode?.label ?? ''))}
        </span>
        <div className='shipment-grid-reference-number-slot'>
          <ShipmentReferenceNumber
            text={record.reference_number || '--'}
            useAvailableWidth={useAvailableWidth}
          />
        </div>
      </div>
    );
  }

  const teams = getTeams(record);

  if (activeLegType === 'EL') {
    return (
      <div className='ref-cell-flex-row-center'>
        {checked && (
          <span
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
            className='shipment-grid-reference-checkbox'
          >
            <Checkbox
              checked={selected}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                event.nativeEvent.stopPropagation();
                onSelectChange(rowId, event.target.checked, record);
              }}
            />
          </span>
        )}

        <span
          className='shipment-grid-reference-leg-icon shipment-grid-reference-leg-icon--el'
          aria-hidden='true'
        >
          {getShipmentLegModeIcon(getLegModeIconType(legMode?.label ?? ''))}
        </span>

        <div className='shipment-grid-reference-el-body'>
          <span className='shipment-grid-reference-el-title'>
            {String(record.reference_number || record.mbl || '--')}
          </span>

          <Flex
            gap={4}
            align='center'
            className='shipment-grid-reference-teams'
            style={{ minWidth: 0 }}
          >
            {teams.length > 0 && (
              <TeamListWithDots teams={toTeamBadges(teams)} />
            )}
          </Flex>
        </div>
      </div>
    );
  }

  return (
    <div className='ref-cell-flex-row-center'>
      {checked && (
        <span
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          className='shipment-grid-reference-select'
        >
          <Checkbox
            checked={selected}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.nativeEvent.stopPropagation();
              onSelectChange(rowId, event.target.checked, record);
            }}
          />
        </span>
      )}
      <Flex className='h-full w-full'>
        <div className='shipment-grid-reference-main'>
          <ShipmentReferenceNumber
            text={getCustomerReference(record)}
            useAvailableWidth={useAvailableWidth}
          />

          <Flex
            gap={4}
            align='center'
            className='shipment-grid-reference-teams'
            style={{ marginTop: 4, minWidth: 0 }}
          >
            {teams.length > 0 && (
              <TeamListWithDots teams={toTeamBadges(teams)} />
            )}
            <ShipmentTablePackageBadge count={record.piece_count} />
          </Flex>
        </div>
      </Flex>
    </div>
  );
};

export const renderTrackingCell = (
  record: ShipmentRow,
  executionLegOptions?: {
    showExecutionLegs?: boolean;
    isExpanded?: boolean;
    onToggle?: () => void;
    customerLegEnabled?: boolean;
    hasOperationalLegView?: boolean;
    dateFormat?: string | null;
  }
) => {
  const hasOperationalLegView = executionLegOptions?.hasOperationalLegView;
  const etas =
    typeof record.etas === 'object' && !Array.isArray(record.etas)
      ? record.etas
      : undefined;
  const stage =
    typeof record.custom_status === 'object' &&
    !Array.isArray(record.custom_status)
      ? (record.custom_status?.current_stage as string | undefined)
      : undefined;
  const stages =
    typeof record.custom_status === 'object' &&
    !Array.isArray(record.custom_status) &&
    Array.isArray(record.custom_status?.stages)
      ? record.custom_status.stages
      : [];
  const estimatedArrival = formatEstimatedArrival(
    record.estimated_delivery_date ?? etas?.estimated_delivery_date,
    record?.eta_timezone || record?.destination_timezone,
    record?.status,
    executionLegOptions?.dateFormat
  );
  const statusText = textOrEmpty(stage || record.status);
  const isDelivered = isDeliveredShipment(record);
  const timelinessPill = isDelivered ? getTimelinessPill(record) : null;
  const statusTagAlerts = (
    Array.isArray(record.alert_status) ? record.alert_status : []
  )
    .filter(
      (alertStatus) =>
        String(alertStatus).toLowerCase() !==
        String(record.status).toLowerCase()
    )
    .join(', ');
  const statusTagText = record.alert_count
    ? [textOrEmpty(record.status as string), statusTagAlerts]
        .filter(Boolean)
        .join(' - ')
    : '';
  const carrierName = formatCarrierName(record?.courier_name);
  const hasCarrier = Boolean(carrierName);
  const carrierLabel = hasCarrier ? carrierName : 'Unknown';
  const courierEtaText = estimatedArrival
    ? `${carrierLabel} ∙ ${estimatedArrival}`
    : carrierLabel;
  const trackingIdText = textOrEmpty(record.courier_tracking_id);
  const executionLegCount = getExecutionLegDisplayCount(record);
  const showExecutionLegs =
    hasOperationalLegView &&
    executionLegOptions?.showExecutionLegs &&
    executionLegCount > 0;

  return (
    <ShipmentMultilineTooltipCell
      tooltipLines={[
        statusText,
        statusTagText,
        courierEtaText,
        stages.length > 0 ? false : trackingIdText,
      ]}
    >
      <Flex
        align='center'
        gap={6}
        className='shipment-grid-tracking-status-row'
      >
        {isDelivered ? (
          <StatusTag
            type={3}
            status='Delivered'
            alert_status={[]}
            className='shipment-grid-status-tag shipment-grid-delivered-tag'
            style={{ borderRadius: 4, ...trackingPillStyle }}
          />
        ) : (
          renderTextCell(statusText, {
            strong: true,
            maxWidth: 140,
            fontSize: 12,
            className: 'shipment-grid-tracking-status-text',
            width: 'auto',
          })
        )}
        {!isDelivered && record.alert_count ? (
          <ShipmentOverflowTooltip
            text={record.status as string}
            className='shipment-grid-alert-status-wrap'
            targetSelector='[data-shipment-tooltip-line]'
          >
            <StatusTag
              type={record.type as StatusType}
              status={record.status as string}
              alert_status={record.alert_status as string[]}
              customerLegEnabled={executionLegOptions?.customerLegEnabled}
              className='shipment-grid-status-tag'
              truncate
              style={{
                borderRadius: 4,
                maxWidth: '100%',
                ...trackingPillStyle,
              }}
              textStyle={{ fontSize: 10, fontWeight: 600 }}
            />
          </ShipmentOverflowTooltip>
        ) : null}
        {timelinessPill ? (
          <ShipmentOverflowTooltip
            text={timelinessPill}
            className='shipment-grid-timeliness-tag-wrap'
            targetSelector='.shipment-grid-timeliness-tag'
          >
            <DeliveryStatusTag
              status={timelinessPill}
              className='shipment-grid-timeliness-tag'
              style={{
                ...trackingPillStyle,
                display: 'inline-block',
                maxWidth: '100%',
              }}
            />
          </ShipmentOverflowTooltip>
        ) : null}
      </Flex>
      {!showExecutionLegs ? (
        hasCarrier ? (
          renderTextCell(courierEtaText, {
            maxWidth: '100%',
            fontSize: 12,
            color: 'var(--text-secondary)',
            className: 'shipment-grid-courier-eta-text',
          })
        ) : (
          <Flex
            align='center'
            gap={4}
            className='shipment-grid-courier-eta-text'
          >
            {renderTextCell('Unknown', {
              width: 'auto',
              maxWidth: '100%',
              fontSize: 12,
              color: 'var(--text-tertiary)',
              style: { flex: '0 0 auto' },
            })}
            {estimatedArrival ? (
              <>
                {renderTextCell('·', {
                  width: 'auto',
                  maxWidth: 8,
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  style: { flex: '0 0 auto' },
                })}
                {renderTextCell(estimatedArrival, {
                  width: 'auto',
                  maxWidth: '100%',
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  style: { flex: '0 1 auto', minWidth: 0 },
                })}
              </>
            ) : null}
          </Flex>
        )
      ) : null}
      {showExecutionLegs ? (
        <Flex align='center' gap={8} className='shipment-grid-customer-leg-row'>
          <button
            type='button'
            className='shipment-grid-customer-leg-toggle'
            onClick={(event) => {
              event.stopPropagation();
              executionLegOptions?.onToggle?.();
            }}
            onDoubleClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            aria-expanded={executionLegOptions?.isExpanded}
            aria-label={
              executionLegOptions?.isExpanded
                ? 'Collapse execution legs'
                : 'Expand execution legs'
            }
          >
            {executionLegOptions?.isExpanded ? (
              <ChevronUpIcon size={12} color='var(--text-primary)' />
            ) : (
              <ChevronDownIcon size={12} color='var(--text-primary)' />
            )}
          </button>
          {renderTextCell(
            `${executionLegCount} ${executionLegCount === 1 ? 'leg' : 'legs'}`,
            {
              width: 'auto',
              maxWidth: 'none',
              fontSize: 12,
              color: 'var(--text-secondary)',
              style: { flex: '0 0 auto' },
            }
          )}
          {renderTextCell('·', {
            width: 'auto',
            maxWidth: 8,
            fontSize: 12,
            color: 'var(--text-secondary)',
          })}
          {renderTextCell(estimatedArrival, {
            maxWidth: '100%',
            fontSize: 12,
            color: 'var(--text-secondary)',
          })}
        </Flex>
      ) : null}
      {stages.length > 0 ? (
        <Flex gap={4} className='shipment-grid-stage-row'>
          {stages.map((stageItem, index) => (
            <span
              key={`${stageItem?.key || index}`}
              className={`shipment-grid-stage-bar ${
                isDelivered
                  ? 'is-delivered'
                  : stageItem?.is_completed
                    ? 'is-completed'
                    : ''
              }`}
            />
          ))}
        </Flex>
      ) : (
        renderTextCell(trackingIdText, {
          maxWidth: 180,
        })
      )}
    </ShipmentMultilineTooltipCell>
  );
};

export const renderLocationCell = (
  record: ShipmentRow,
  key: 'from_location' | 'to_location',
  useAvailableWidth = false
) => {
  const location = record[key];
  const isOrigin = key === 'from_location';
  const etas =
    typeof record.etas === 'object' && !Array.isArray(record.etas)
      ? record.etas
      : undefined;
  const date = isOrigin
    ? record.actual_pickup_date ||
      record.scheduled_pickup_date ||
      etas?.actual_pickup_date ||
      etas?.scheduled_pickup_date
    : record.actual_delivery_date ||
      record.scheduled_delivery_date ||
      etas?.actual_delivery_date ||
      etas?.scheduled_delivery_date;
  const locationName = getLocationName(
    location,
    isOrigin ? 'Origin' : 'Destination'
  );
  const address = getShipmentLocationParsedAddress(location as never);
  const dateText = textOrEmpty(date);

  return (
    <ShipmentMultilineTooltipCell
      tooltipLines={[locationName, address, dateText]}
      className='shipment-grid-location-cell'
    >
      {renderTextCell(locationName, {
        strong: true,
        maxWidth: useAvailableWidth ? '100%' : 170,
        fontSize: 12,
        style: {
          lineHeight: '16px',
        },
      })}
      {renderTextCell(address ? address : 'Unknown', {
        maxWidth: '100%',
        fontSize: 12,
        color: address ? 'var(--text-secondary)' : 'var(--text-tertiary)',
        className: 'shipment-grid-location-address',
        style: {
          fontWeight: 400,
          minHeight: 20,
        },
      })}
      {renderTextCell(dateText ? dateText : 'Unknown', {
        maxWidth: '100%',
        fontSize: 10,
        color: dateText ? 'var(--text-secondary)' : 'var(--text-tertiary)',
        style: {
          fontWeight: 400,
          lineHeight: '16px',
          minHeight: 16,
        },
      })}
    </ShipmentMultilineTooltipCell>
  );
};

export const renderProductsCell = (record: ShipmentRow) => {
  const products = Array.isArray(record.products) ? record.products : [];
  if (products.length === 0) return null;
  if (products.length === 1) {
    return renderTextCell(textOrEmpty(products[0]?.name), { maxWidth: 180 });
  }
  const productNames = products
    .map((product) => product?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <ShipmentTableProductCell
      label={`${products.length} products`}
      productNames={productNames}
    />
  );
};

export const renderTeamsCell = (record: ShipmentRow) => {
  const teams = getTeams(record);
  if (!teams.length) return null;

  return (
    <Flex gap={4} align='center'>
      <TeamListWithDots teams={toTeamBadges(teams)} />
    </Flex>
  );
};

export const renderActionCell = (
  record: ShipmentRow,
  onShipmentAction?: (
    key: ShipmentTableActionKey,
    shipment: ShipmentRow,
    meta?: {
      CoC_ID?: string | null;
      mentionsMe?: boolean;
      scrollToCommentsBottom?: boolean;
    }
  ) => void,
  allPermissions?: {
    is_view?: boolean;
    is_update?: boolean;
    [key: string]: unknown;
  },
  activeLegType?: legTypes
) => {
  const canSaveNotification =
    record.can_save_notification === undefined ||
    Boolean(record.can_save_notification);
  const showShipmentActivity =
    allPermissions?.is_view &&
    activeLegType !== 'EL' &&
    record.leg_type !== 'EL' &&
    !isElSubRow(record);
  const openComments = (
    commentId?: string | null,
    meta?: { mentionsMe?: boolean; scrollToCommentsBottom?: boolean }
  ) => {
    onShipmentAction?.('open_comments', record, {
      CoC_ID: commentId || null,
      mentionsMe: meta?.mentionsMe,
      scrollToCommentsBottom: meta?.scrollToCommentsBottom,
    });
  };
  return (
    <Flex
      align='center'
      justify='end'
      gap={8}
      className='shipment-grid-action'
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      {showShipmentActivity && (
        <ShipmentActivityCell shipment={record} onOpenComments={openComments} />
      )}

      {allPermissions?.is_view && (
        <Dropdown
          trigger={['click']}
          placement='bottomRight'
          dropdownRender={() => (
            <div className='CT-shipment-table-actions'>
              {MENU_ITEMS.map((item) => {
                if (
                  (activeLegType === 'EL' || isElSubRow(record)) &&
                  item.key !== 'product_list' &&
                  item.key !== 'map_full_screen'
                ) {
                  return null;
                }
                // Permission checks for each menu item
                if (
                  item.key === 'edit_shipment' &&
                  !record?.permissions?.is_update
                ) {
                  return null;
                }
                if (item.key === 'setup_notification' && !canSaveNotification) {
                  return null;
                }
                if (
                  item.key === 'one_time_message' &&
                  !allPermissions?.is_view
                ) {
                  return null;
                }
                if (
                  item.key === 'share_tracking_link' &&
                  !allPermissions?.is_update
                ) {
                  return null;
                }
                if (
                  item.key === 'dismiss_alerts' &&
                  record.can_dismiss_alerts !== true
                ) {
                  return null;
                }
                if (
                  (item.key === 'mark_as_delivered' ||
                    item.key === 'archive_shipment') &&
                  record.can_manage_record !== true
                ) {
                  return null;
                }

                const isDisabled =
                  record?.products?.length === 0 && item.key === 'product_list';

                // Handle export_coc case with ExportCOCWrapper
                if (item.key === 'export_coc') {
                  return (
                    <div key={item.key}>
                      <ExportCOCWrapper shipment={record} />
                    </div>
                  );
                }

                return (
                  <Flex
                    justify='space-between'
                    align='center'
                    key={item.key}
                    className={`header-menu-item ${isDisabled && 'disabled-row'}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onShipmentAction?.(item.key, record);
                    }}
                    style={{
                      color: item.danger ? 'var(--danger)' : undefined,
                    }}
                  >
                    <Flex>
                      {item.icon}
                      <span
                        className='header-menu-item-content'
                        // eslint-disable-next-line react/forbid-dom-props -- colour depends on item.danger
                        style={{
                          color: item.danger ? 'var(--danger)' : undefined,
                        }}
                      >
                        {item.label}
                      </span>
                    </Flex>

                    {item?.expand && (item.key as string) !== 'export_coc' && (
                      <ChevronRightIcon
                        fill='var(--text-primary)'
                        width={16}
                        height={16}
                      />
                    )}
                  </Flex>
                );
              })}
              {allPermissions?.is_update &&
                activeLegType !== 'EL' &&
                !isElSubRow(record) && (
                  <SchedulePickupMenuItem
                    shipment={record}
                    onSelect={() =>
                      onShipmentAction?.('schedule_pickup', record)
                    }
                  />
                )}
            </div>
          )}
        >
          <span
            className='shipment-grid-action shipment-grid-action--menu'
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <DotsIcon color='var(--text-primary)' width={14} height={14} />
          </span>
        </Dropdown>
      )}
    </Flex>
  );
};
